import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  marketingColor,
  marketingFont,
  marketingLayout,
  marketingLine,
  marketingShadow,
} from '../../theme/marketingTokens';
import { marketingEvent } from '../../constants/marketingEvent';
import { Eyebrow, ctaButtonSx } from './MarketingPrimitives';

const bandSx = {
  backgroundColor: marketingColor.surface,
  borderTop: `1px solid ${marketingLine.hairline}`,
  borderBottom: `1px solid ${marketingLine.hairline}`,
} as const;

/**
 * Placeholder retailer wordmarks.
 * The handoff flags these for replacement with real partner logos before launch.
 */

export const ValueProps: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string, options?: any) => any };
  const items = t('public.home.valueProps.items', { returnObjects: true }) as {
    title: string;
    description: string;
  }[];

  return (
    <Box
      component="section"
      sx={{
        maxWidth: marketingLayout.maxWidth,
        mx: 'auto',
        px: marketingLayout.sectionPaddingX,
        py: { xs: 8, md: 12.5 },
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 7 }}>
        <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.25rem' }, mb: 1.75 }}>
          {t('public.home.valueProps.title')}
        </Typography>
        <Typography sx={{ fontSize: 15, color: marketingColor.textSecondary }}>
          {t('public.home.valueProps.subtitle')}
        </Typography>
      </Box>
      {/*
        Hairline grid: a 1px gap over a hairline-coloured background, so the
        gaps themselves become the single-pixel dividers between cards.
      */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(auto-fit, minmax(280px, 1fr))' },
          gap: '1px',
          backgroundColor: marketingLine.hairline,
          border: `1px solid ${marketingLine.hairline}`,
        }}
      >
        {(Array.isArray(items) ? items : []).map((item, index) => (
          <Box key={item.title} sx={{ backgroundColor: marketingColor.surface, p: 4 }}>
            <Typography
              sx={{
                fontFamily: marketingFont.mono,
                fontWeight: 700,
                fontSize: 26,
                color: marketingColor.accent,
                mb: 2.25,
              }}
            >
              {String(index + 1).padStart(2, '0')}
            </Typography>
            <Typography
              sx={{
                fontFamily: marketingFont.display,
                fontSize: 17,
                fontWeight: 700,
                color: marketingColor.text,
                mb: 1.25,
              }}
            >
              {item.title}
            </Typography>
            <Typography sx={{ fontSize: 14, color: marketingColor.textSecondary, lineHeight: 1.65 }}>
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const PartnerEvent: React.FC<{ onBookMeeting: () => void }> = ({ onBookMeeting }) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box component="section" sx={{ ...bandSx, px: marketingLayout.sectionPaddingX, py: { xs: 8, md: 11.5 } }}>
      <Box
        sx={{
          maxWidth: 1000,
          mx: 'auto',
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Box sx={{ flex: 1.2, minWidth: 300 }}>
          <Eyebrow sx={{ mb: 2.75 }}>{t('public.home.partner.eyebrow')}</Eyebrow>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 2 }}>
            {t('public.home.partner.title')}
          </Typography>
          <Typography
            sx={{ fontSize: 14.5, color: marketingColor.textSecondary, lineHeight: 1.7, mb: 1.75 }}
          >
            {t('public.home.partner.body1')}
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: marketingColor.textSecondary, lineHeight: 1.7 }}>
            {t('public.home.partner.body2')}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 280,
            backgroundColor: marketingColor.ink,
            border: `1px solid ${marketingLine.hairlineStrong}`,
            borderRadius: '8px',
            p: 3.5,
            boxShadow: marketingShadow.card,
          }}
        >
          <Typography
            sx={{
              fontFamily: marketingFont.mono,
              fontSize: 10.5,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: marketingColor.accent,
              mb: 1.75,
            }}
          >
            {t('public.home.partner.whereToFindUs')}
          </Typography>
          <Typography
            sx={{
              fontFamily: marketingFont.display,
              fontSize: 19,
              fontWeight: 700,
              color: marketingColor.text,
              lineHeight: 1.3,
              mb: 0.75,
            }}
          >
            {marketingEvent.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: marketingFont.mono,
              fontSize: 13,
              fontWeight: 700,
              color: marketingColor.accent,
              mb: 2.25,
            }}
          >
            {marketingEvent.datesLabel}
          </Typography>
          <Box sx={{ height: '1px', backgroundColor: 'rgba(95,226,138,0.2)', mb: 2.25 }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: marketingColor.text, mb: 0.5 }}>
            {marketingEvent.venue}
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: marketingColor.textSecondary, lineHeight: 1.6, mb: 2.75 }}
          >
            {marketingEvent.addressLine}
            <br />
            {marketingEvent.cityState}
          </Typography>
          <Button disableElevation fullWidth sx={{ ...ctaButtonSx, fontSize: 13 }} onClick={onBookMeeting}>
            {t('public.home.partner.cta')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Closing call-to-action band. Defaults to the home page's copy; pass a
 * `copyKey` to run the same band with another page's wording.
 */
export const HomeCta: React.FC<{ onBookDemo: () => void; copyKey?: string }> = ({
  onBookDemo,
  copyKey = 'public.home.cta',
}) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box
      component="section"
      sx={{
        px: marketingLayout.sectionPaddingX,
        py: { xs: 8, md: 11.25 },
        textAlign: 'center',
        borderBottom: `1px solid ${marketingLine.hairline}`,
      }}
    >
      <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.125rem' }, mb: 1.75 }}>
        {t(`${copyKey}.title`)}
      </Typography>
      <Typography sx={{ fontSize: 14.5, color: marketingColor.textSecondary, mb: 3.75 }}>
        {t(`${copyKey}.subtitle`)}
      </Typography>
      <Button disableElevation sx={{ ...ctaButtonSx, px: 4, py: 2 }} onClick={onBookDemo}>
        {t(`${copyKey}.button`)}
      </Button>
    </Box>
  );
};
